import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { getApprovalLineMembersUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import useDeactivatedApprovers from '@/hooks/approval/useDeactivatedApprovers';
import {
  indexLineMemberStatuses,
  lineMemberStateFromDeactivatedIds,
  lookupLineMemberState,
  shouldUseLineMemberFallback,
  type ApprovalLineMemberKey,
  type ApprovalLineMemberState,
  type ApprovalLineMemberStatusResponse,
} from '@/utils/approval/approvalLineMemberStatus';

/**
 * 조회 화면 결재라인 인물의 현재 재직·휴직 상태.
 *
 * 상신 시점에 박제된 결재라인 정보와 달리 조회 시점 프로필을 읽으므로 오래된 문서에서도
 * 지금 상태가 내려온다. 작성자까지 포함해 한 번에 받으므로 문서를 열 때 호출은 한 번이다.
 *
 * 이 API 가 없는 환경(프론트가 백엔드보다 먼저 배포된 경우)에서는 기존 퇴사자 조회로
 * 물러선다 — 휴직은 못 보여주더라도 기존 '퇴사' 배지까지 사라지지는 않게.
 */
const useApprovalLineMembers = (documentId?: string | null) => {
  const { data, error, isLoading } = useSWR<ApprovalLineMemberStatusResponse[]>(
    documentId ? getApprovalLineMembersUrl(documentId) : null,
    getNestedDataFetcher,
    // 이 API 가 아직 없는 환경에서는 문서를 열 때마다 실패 요청이 무한 백오프로 쌓인다.
    // 한 번 실패하면 폴백으로 넘어가면 되므로 재시도하지 않는다.
    { shouldRetryOnError: false },
  );

  const useFallback = shouldUseLineMemberFallback(error, data);

  const { deactivatedIds: fallbackDeactivatedIds } = useDeactivatedApprovers(
    useFallback ? documentId : null,
  );

  const statusIndex = useMemo(() => indexLineMemberStatuses(data), [data]);

  const getMemberState = useCallback(
    (person: ApprovalLineMemberKey): ApprovalLineMemberState =>
      useFallback
        ? lineMemberStateFromDeactivatedIds(fallbackDeactivatedIds, person)
        : lookupLineMemberState(statusIndex, person),
    [statusIndex, useFallback, fallbackDeactivatedIds],
  );

  return { getMemberState, isLoading, error };
};

export default useApprovalLineMembers;
